// Franja superior de la vista Cartera: una tarjeta por cobrador con su carga activa contra el tope
// de la regla vigente. Sin tope no hay barra ni porcentaje — «sin tope» no es 0 ni 100 %.
import { AlertTriangle } from 'lucide-react';
import { Button, ErrorState, Loading } from '@/kit';
import { userMessageOf } from '@/api/errors';
import { cn } from '@/lib/cn';
import { CollectorSeniorityEnum } from '@fiado/type-kit/bin/retailOrg/enums/CollectorSeniorityEnum';
import {
    describeCollector,
    describeCoverage,
    effectiveThreshold,
    isOverThreshold,
    loadRatio,
    NO_THRESHOLD_LABEL,
    sortCollectorLoads,
} from '../logic';
import type { CollectorLoadRow } from '../types';

const CARD =
    'flex w-[210px] flex-none flex-col gap-1.5 rounded-[10px] border bg-card px-3 py-2 text-left';

/** Etiqueta es-MX de la veteranía; la estrategia BY_SENIORITY reparte con este dato. */
const SENIORITY_LABEL: Record<CollectorSeniorityEnum, string> = {
    [CollectorSeniorityEnum.JUNIOR]: 'Junior',
    [CollectorSeniorityEnum.SENIOR]: 'Senior',
};

function LoadBar({ row }: { row: CollectorLoadRow }) {
    const ratio = loadRatio(row);
    const threshold = effectiveThreshold(row.threshold);
    if (ratio === null || threshold === null) return null;
    return (
        <div
            role="progressbar"
            aria-label="Carga contra el tope"
            aria-valuemin={0}
            aria-valuemax={threshold}
            aria-valuenow={row.activeAssignments}
            className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        >
            <div
                className={cn('h-full rounded-full', ratio >= 1 ? 'bg-destructive' : 'bg-primary')}
                style={{ width: `${String(Math.min(100, Math.round(ratio * 100)))}%` }}
            />
        </div>
    );
}

function CollectorCard({
    row,
    selected,
    canRedistribute,
    onSelect,
    onRedistribute,
}: {
    row: CollectorLoadRow;
    selected: boolean;
    canRedistribute: boolean;
    onSelect: (collectorUserId: string) => void;
    onRedistribute: (row: CollectorLoadRow) => void;
}) {
    const collector = describeCollector(row.collectorUserId, row.displayName, row.presence);
    const coverage = describeCoverage(row);
    const threshold = effectiveThreshold(row.threshold);
    const overThreshold = isOverThreshold(row);
    return (
        <li>
            <div
                className={cn(
                    CARD,
                    selected ? 'border-primary bg-primary/[0.06]' : 'border-border',
                    overThreshold && !selected && 'border-destructive/50',
                )}
            >
                <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                        onSelect(row.collectorUserId);
                    }}
                    className="flex flex-col gap-1 text-left"
                >
                    <span
                        className={cn(
                            'truncate text-[12.5px] font-semibold',
                            !collector.isName && 'font-mono text-[11px]',
                        )}
                    >
                        {collector.text}
                    </span>
                    {collector.note === null ? null : (
                        <span className="text-[10px] italic text-muted-foreground">
                            {collector.note}
                        </span>
                    )}
                    <span className="text-[11.5px] tabular-nums">
                        <strong className="font-bold">{row.activeAssignments}</strong>
                        {' activas · tope '}
                        {threshold ?? NO_THRESHOLD_LABEL}
                    </span>
                    <LoadBar row={row} />
                    {overThreshold ? (
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-destructive">
                            <AlertTriangle className="size-3" aria-hidden />
                            Llegó a su tope
                        </span>
                    ) : null}
                    {row.seniority === null ? null : (
                        <span className="text-[10px] text-muted-foreground">
                            Veteranía: {SENIORITY_LABEL[row.seniority]}
                        </span>
                    )}
                    <span
                        title={coverage.text}
                        className="truncate text-[10px] text-muted-foreground"
                    >
                        {coverage.text}
                        {coverage.note === null ? null : <em> · {coverage.note}</em>}
                    </span>
                </button>
                {canRedistribute ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-[11px]"
                        onClick={() => {
                            onRedistribute(row);
                        }}
                    >
                        Redistribuir cartera
                    </Button>
                ) : null}
            </div>
        </li>
    );
}

export interface CollectorLoadsStripProps {
    rows: readonly CollectorLoadRow[];
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    /** Cobrador por el que la lista está filtrada; su tarjeta queda marcada. */
    selectedCollectorUserId: string | null;
    onSelect: (collectorUserId: string) => void;
    canRedistribute: boolean;
    onRedistribute: (row: CollectorLoadRow) => void;
}

export function CollectorLoadsStrip({
    rows,
    isLoading,
    error,
    onRetry,
    selectedCollectorUserId,
    onSelect,
    canRedistribute,
    onRedistribute,
}: CollectorLoadsStripProps) {
    if (isLoading) return <Loading text="Cargando la carga de los cobradores…" />;

    // `ErrorState` ya trae `role="alert"`: la lectura caída no se confunde con «no hay cobradores».
    if (error) {
        return (
            <ErrorState
                title="No se pudo cargar la carga de los cobradores"
                description={userMessageOf(error)}
                onRetry={onRetry}
            />
        );
    }

    if (rows.length === 0) {
        return (
            <p className="rounded-[10px] border border-dashed border-border px-3 py-3 text-[11.5px] text-muted-foreground">
                Esta cadena no tiene cobradores con cartera activa.
            </p>
        );
    }

    return (
        <ul aria-label="Carga por cobrador" className="flex gap-2 overflow-x-auto pb-1">
            {sortCollectorLoads(rows).map((row) => (
                <CollectorCard
                    key={row.collectorUserId}
                    row={row}
                    selected={row.collectorUserId === selectedCollectorUserId}
                    canRedistribute={canRedistribute}
                    onSelect={onSelect}
                    onRedistribute={onRedistribute}
                />
            ))}
        </ul>
    );
}
